import { ReactElement, useState, useRef, useEffect } from 'react';
import classnames from 'classnames/bind';
import { ArrowIcon, Typography } from '@weareopenr/component-library';
import styles from './CollapsibleSection.module.scss';

const cx = classnames.bind(styles);

interface ICollapsibleSection {
  number?: string;
  title: string;
  subtitle?: string | JSX.Element;
  children?: ReactElement;
  size?: 'small' | 'large';
  className?: string;
}

const CollapsibleSection = ({ number, title, subtitle, children, size = 'large', className }: ICollapsibleSection) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const contentRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  useEffect(() => {
    const contentHeight = contentRef?.current?.children?.[0]?.clientHeight;

    if (isExpanded && contentHeight > 250) {
      setIsScrollable(true);
    } else {
      setIsScrollable(false);
    }
  }, [isExpanded]);

  return (
    <div className={cx('collapsible-section', className)}>
      <div className={styles['header-container']} onClick={() => setIsExpanded((prev: boolean) => !prev)}>
        <div className={styles['header']}>
          {number && (
            <div className={styles['number']}>
              <Typography colour="secondary" weight="bold">
                {number}
              </Typography>
            </div>
          )}
          <div className={styles['title']}>
            <Typography size={size === 'small' ? 'medium' : 'xlarge'} weight="semi-bold">
              {title}
            </Typography>
          </div>
          {children && (
            <button
              className={styles['arrow-button']}
              aria-expanded={isExpanded}
              type="button"
              aria-label="arrow-button"
            >
              <ArrowIcon
                className={cx('arrow', {
                  'arrow-down': !isExpanded,
                })}
              />
            </button>
          )}
        </div>
        {subtitle && (
          <div className={cx({ 'margin-left': number })}>
            {typeof subtitle === 'string' ? <Typography>{subtitle}</Typography> : <>{subtitle}</>}
          </div>
        )}
      </div>
      <div
        className={cx('collapsible-body', {
          'margin-left': number,
          'collapsible-body-expanded': isExpanded,
          'is-scrollable': isScrollable,
        })}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
